import { Link } from "react-router-dom";
import { ChevronRight, ArrowUp } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";

/**
 * Shared layout for all long-form legal / policy pages. Provides:
 *  - Consistent hero, "Last updated" strip, and container widths
 *  - Sticky in-page table of contents on desktop
 *  - Cross-links to sibling documents at the foot of every page
 *
 * Content authors just pass an array of ``sections`` — each section renders
 * with an anchor id so the TOC links jump correctly.
 */
export function LegalShell({
  eyebrow = "Legal",
  title,
  intro,
  lastUpdated,
  sections,
  siblings = DEFAULT_SIBLINGS,
  testid = "legal-shell",
}) {
  return (
    <SiteShell>
      <section className="container-page py-12 sm:py-16" data-testid={testid}>
        <div className="max-w-5xl">
          <span className="chip inline-flex items-center gap-1.5">{eyebrow}</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">{title}</h1>
          {intro && (
            <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">{intro}</p>
          )}
          <div className="mt-4 text-xs text-muted-foreground">
            Last updated: <span className="font-medium text-foreground">{lastUpdated}</span>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Sticky TOC on desktop */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
                On this page
              </div>
              <ul className="space-y-1.5 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground link-underline"
                    >
                      <ChevronRight size={12} /> {s.title}
                    </a>
                  </li>
                ))}
              </ul>
              <a
                href="#top"
                className="mt-6 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ArrowUp size={12} /> Back to top
              </a>
            </div>
          </aside>

          <article className="max-w-3xl" id="top">
            <div className="prose prose-neutral prose-sm sm:prose-base max-w-none">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-28">
                  <h2 className="font-serif text-2xl sm:text-3xl mt-10 first:mt-0">
                    {s.title}
                  </h2>
                  <div className="mt-3 text-foreground/90 leading-relaxed space-y-3">
                    {s.body}
                  </div>
                </section>
              ))}
            </div>

            {/* Sibling nav */}
            <div className="mt-16 border-t pt-8">
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Related documents
              </div>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                {siblings.map((s) => (
                  <li key={s.to}>
                    <Link
                      to={s.to}
                      className="block rounded-xl border p-3 hover:bg-muted/40 transition-colors"
                      data-testid={`legal-sibling-${s.to.replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      <div className="font-medium">{s.label}</div>
                      {s.hint && <div className="text-xs text-muted-foreground mt-0.5">{s.hint}</div>}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-muted-foreground">
                Questions about this document? Email{" "}
                <a href="mailto:hello@lastbookmark.com" className="link-underline">
                  hello@lastbookmark.com
                </a>{" "}
                and we'll get back to you.
              </p>
            </div>
          </article>
        </div>
      </section>
    </SiteShell>
  );
}

export const DEFAULT_SIBLINGS = [
  { to: "/beta-terms", label: "Beta Terms", hint: "What being on the beta means for you." },
  { to: "/terms", label: "Terms of Service", hint: "The rules of using Last Bookmark." },
  { to: "/privacy", label: "Privacy Policy", hint: "What we collect and how we protect it." },
  { to: "/community-guidelines", label: "Community Guidelines", hint: "How we treat each other here." },
  { to: "/payment-guidance", label: "Payment guidance", hint: "How to safely arrange payment during beta." },
  { to: "/contact", label: "Contact support", hint: "Reach the founding team." },
];
